<template>
    <div class="automation-wizard-field-select">
        <CompassSkeleton
            v-show="showSkeleton"
            size="x-large"
            class="automation-wizard-field-select__skeleton"
        />
        <CompassFormItem
            v-show="!showSkeleton"
            v-bind="formItemProps"
            :error="field.currentError || ''"
        >
            <CompassSelect
                v-bind="selectProps"
                :key="`field-select-${options.length}`"
                :value="currentValue"
                :loading="loading"
                :disabled="disabled"
                menuClass="automation-wizard-field-select"
                @update:value="handleValueChange"
            >
                <CompassDropdownSelectOption
                    v-for="option in options"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                />
            </CompassSelect>
        </CompassFormItem>
    </div>
</template>

<script>
import {
    getStaffs,
    getTaskLabels,
    getOpportunitySources,
} from 'api/automation/automationService.js';

export default {
    props: {
        field: {
            type: Object,
            required: true,
        },
        value: {
            type: [String, Number, Object, Array],
            default: '',
        },
    },
    emits: ['update:value'],
    data() {
        return {
            options: [],
            loading: false,
        };
    },
    computed: {
        formItemProps() {
            return {
                ...this.field.props?.formItem,
                label: this.field.props?.formItem?.label || '',
                required: this.field.required || false,
                requiredLabel:
                    this.field.props?.formItem?.requiredLabel ||
                    this.$t.__('common.labels.required'),
            };
        },
        selectProps() {
            return {
                ...this.field.props?.select,
            };
        },
        disabled() {
            return this.field.disabled || false;
        },
        showSkeleton() {
            return this.loading && this.options.length === 0;
        },
        currentValue() {
            if (this.selectProps?.multiple) {
                return this.filterValidValues(this.value);
            } else {
                return this.isValidValue(this.value) ? this.value : '';
            }
        },
    },
    watch: {
        async value() {
            if (this.options.length === 0) {
                await this.loadOptions();
            }
        },
    },
    async mounted() {
        await this.loadOptions();
    },
    methods: {
        isValidValue(value) {
            const validOptionValues = new Set(this.options.map((option) => option.value));
            return validOptionValues.has(value);
        },
        filterValidValues(values) {
            const validOptionValues = new Set(this.options.map((option) => option.value));
            return Array.isArray(values)
                ? values.filter(
                      (value) =>
                          value !== null && value !== undefined && validOptionValues.has(value),
                  )
                : [];
        },
        handleValueChange(newValue) {
            let cleanValue;
            if (this.selectProps?.multiple) {
                cleanValue = this.filterValidValues(newValue);
            } else {
                cleanValue = this.isValidValue(newValue) ? newValue : '';
            }
            this.$emit('update:value', cleanValue);
        },
        async loadOptions() {
            try {
                this.loading = true;
                let options = [];
                let allOptions = [];
                let defaultValue = '';
                // Handle prepend values first if they exist
                if (this.field.prependValues && Array.isArray(this.field.prependValues)) {
                    const prependOptions = this.field.prependValues.map((item) => ({
                        label: item.value,
                        value: item.key,
                    }));
                    options = [...prependOptions];
                }
                // Add custom options from field.values if they exist
                if (this.field.values && Array.isArray(this.field.values)) {
                    const fieldOptions = this.field.values.map((item) => ({
                        label: item.value,
                        value: item.key,
                    }));
                    allOptions = [...allOptions, ...fieldOptions];
                }
                // Add dynamic options from API if objectType exists
                let apiOptions = [];
                if (this.field.objectType) {
                    apiOptions = await this.loadOptionsFromApi(this.field.objectType);
                    allOptions = [...allOptions, ...apiOptions];
                    if (this.field.objectType === 'sourceOpp' && Array.isArray(apiOptions)) {
                        const defaultOption = apiOptions.find((opt) => opt.isDefault);
                        if (defaultOption) {
                            defaultValue = defaultOption.value;
                        }
                    }
                }
                allOptions.sort((a, b) => a.label.localeCompare(b.label));
                options = [...options, ...allOptions];
                this.options = options;
                this.$nextTick(() => {
                    if (defaultValue && !this.value) {
                        this.$emit('update:value', defaultValue);
                    }
                });
            } catch (error) {
                this.options = [];
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async loadOptionsFromApi(objectType) {
            try {
                switch (objectType) {
                    case 'staffs':
                        return await this.loadStaffsOptions();
                    case 'labelTask':
                        return await this.loadLabelTasksOptions();
                    case 'sourceOpp':
                        return await this.loadOpportunitySourcesOptions();
                    default:
                        return [];
                }
            } catch (error) {
                return [];
            }
        },
        async loadStaffsOptions() {
            const response = await getStaffs();
            return response.data.map((staff) => ({
                label: `${staff.firstname} ${staff.lastname}`,
                value: staff.id,
            }));
        },
        async loadLabelTasksOptions() {
            const response = await getTaskLabels();
            return response.data.map((label) => ({
                label: label.name,
                value: label.id,
            }));
        },
        async loadOpportunitySourcesOptions() {
            const response = await getOpportunitySources();
            return response.data.map((source) => ({
                label: source.name,
                value: source.id,
                isDefault: !!source.is_default,
            }));
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-wizard-field-select {
    &__skeleton {
        width: 100%;
        ::v-deep .compass-skeleton__line--x-large {
            height: 56px;
        }
    }
}
</style>

<style lang="scss">
#compass-dropdown-container {
    .automation-wizard-field-select {
        z-index: 1001;
    }
}
</style>
